// OmniRoute / 9Router / hosted-inference provider registry — shared spec:
// /Volumes/Sanwidi 2TB/02-projects/sidehustle/_shared/llm-routers/SPEC.md
//
// Pure ESM, no React/DOM imports — uses globalThis.fetch (call-time, not captured)
// so callers (browser) and tests (Node + a stubbed/real fetch) both work unchanged.

export const PRESETS = [
  {
    id: 'omniroute',
    label: 'OmniRoute',
    baseUrl: 'http://localhost:20128/v1',
    authHeader: 'Authorization',
    // Keyless does NOT work from a browser: OmniRoute only echoes the page's Origin
    // (CORS) on requests that carry a key, and /v1/models 401s without one. Proven
    // against omniroute@3.8.50 on 26 Sep 2026 (docs/ROUTER-PROOF-2026-09-26.md).
    requiresKey: true,
    modelsEndpoint: '/models',
    defaultModels: { cheap: 'auto', strong: 'auto' },
    notes: 'key dari dashboard OmniRoute (menu API Keys), port 20128',
  },
  {
    id: '9router',
    label: '9Router',
    baseUrl: 'http://localhost:20128/v1',
    authHeader: 'Authorization',
    requiresKey: true,
    modelsEndpoint: '/models',
    defaultModels: { cheap: '', strong: '' },
    notes: 'key dari dashboard lokal, port 20128, jangan jalan bareng OmniRoute',
  },
  {
    id: 'openrouter',
    label: 'OpenRouter',
    baseUrl: 'https://openrouter.ai/api/v1',
    authHeader: 'Authorization',
    requiresKey: true,
    modelsEndpoint: '/models',
    defaultModels: { cheap: 'meta-llama/llama-3.1-8b-instruct', strong: 'anthropic/claude-sonnet-4.5' },
    notes: '',
  },
  {
    id: 'groq',
    label: 'Groq',
    baseUrl: 'https://api.groq.com/openai/v1',
    authHeader: 'Authorization',
    requiresKey: true,
    modelsEndpoint: '/models',
    defaultModels: { cheap: 'openai/gpt-oss-20b', strong: 'openai/gpt-oss-120b' },
    notes: '',
  },
  {
    id: 'together',
    label: 'Together',
    baseUrl: 'https://api.together.xyz/v1',
    authHeader: 'Authorization',
    requiresKey: true,
    modelsEndpoint: '/models',
    defaultModels: { cheap: 'meta-llama/Llama-3.2-3B-Instruct-Turbo', strong: 'deepseek-ai/DeepSeek-V3' },
    notes: '',
  },
  {
    id: 'fireworks',
    label: 'Fireworks',
    baseUrl: 'https://api.fireworks.ai/inference/v1',
    authHeader: 'Authorization',
    requiresKey: true,
    modelsEndpoint: '/models',
    defaultModels: { cheap: 'accounts/fireworks/models/llama-v3p1-8b-instruct', strong: 'accounts/fireworks/models/kimi-k2-instruct-0905' },
    notes: '',
  },
  {
    id: 'deepinfra',
    label: 'DeepInfra',
    baseUrl: 'https://api.deepinfra.com/v1/openai',
    authHeader: 'Authorization',
    requiresKey: true,
    modelsEndpoint: '/models',
    defaultModels: { cheap: 'meta-llama/Llama-3.1-8B-Instruct', strong: 'deepseek-ai/DeepSeek-V3' },
    notes: '',
  },
];

export const CUSTOM_PRESET = {
  id: 'custom',
  label: 'Custom OpenAI-compatible',
  baseUrl: '',
  authHeader: 'Authorization',
  requiresKey: true,
  modelsEndpoint: '/models',
  defaultModels: { cheap: '', strong: '' },
  notes: '',
};

// Providers kept behind the collapsed "Lanjutan" section (San, 27 Sep 2026): local
// routers and the custom endpoint stay supported, but the default provider list never
// shows or promotes them. The full app's "Router / API lain" tile is gated the same way.
export const ADVANCED_PROVIDER_IDS = ['omniroute', '9router', 'custom'];

export function isAdvancedProvider(id) {
  return ADVANCED_PROVIDER_IDS.includes(id);
}

export function getPreset(id) {
  if (id === 'custom') return CUSTOM_PRESET;
  return PRESETS.find((p) => p.id === id) || null;
}

// Hosts the bridge (bridge/qlipper_bridge.py's /or handler) is willing to proxy to.
// Anything else (local routers, custom endpoints, unknown hosts) goes browser-direct.
export const BRIDGE_HOSTS = ['openrouter.ai', 'api.groq.com', 'api.together.xyz', 'api.fireworks.ai', 'api.deepinfra.com'];

function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^\[|\]$/g, '');
  } catch {
    return '';
  }
}

// True for localhost / LAN / *.local hosts — these can never be reached from a
// server-side bridge (they're only reachable from the user's own machine/network).
export function isLocalBase(url) {
  const host = hostnameOf(url);
  if (!host) return false;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return true;
  if (/\.local$/i.test(host)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
  return false;
}

// GET {baseUrl}{modelsEndpoint} with the auth header; on any non-2xx, empty result,
// or network error, fall back to preset.defaultModels and flag usedDefaults so the
// UI never silently pretends discovery succeeded.
export async function discoverModels(preset, apiKey, baseUrl = preset.baseUrl) {
  try {
    const headers = apiKey ? { [preset.authHeader]: `Bearer ${apiKey}` } : {};
    const res = await globalThis.fetch(String(baseUrl).replace(/\/$/, '') + preset.modelsEndpoint, {
      headers,
      signal: AbortSignal.timeout(10000),
    });
    if (!res.ok) throw new Error(String(res.status));
    const data = await res.json();
    // ids may come back as { id } objects (OpenAI-style) or as plain strings — accept both.
    const models = [...new Set(
      (data && Array.isArray(data.data) ? data.data : [])
        .map((m) => (typeof m === 'string' ? m : m && m.id))
        .filter(Boolean)
    )];
    if (!models.length) throw new Error('no models in response');
    return { models, usedDefaults: false };
  } catch {
    // .filter(Boolean) drops empty-string entries (9router/custom's defaultModels are
    // {cheap:'', strong:''} — no sane default without a discovered model); [...new
    // Set(...)] dedupes presets whose cheap/strong defaults are IDENTICAL (omniroute's
    // {cheap:'auto', strong:'auto'}), so the UI never renders two chips/options sharing
    // one React key.
    return { models: [...new Set(Object.values(preset.defaultModels).filter(Boolean))], usedDefaults: true };
  }
}

// One cheap call to prove base URL + key work before saving a preset. Mirrors
// chat.js's chatCompletion routing rule: a hosted provider the browser can't reach
// directly (no CORS headers) goes through the same /yt/or bridge chat.js itself uses
// at analysis time, so "Tes koneksi" exercises the SAME path the real call will take —
// otherwise a keyless OpenRouter test (works via the bridge's server key) goes
// browser-direct with no Authorization header, 401s, and shows a false "key salah"
// red badge while analysis actually works fine.
// max_tokens for the connection-test ping. NOT 1: a "thinking" model (e.g. Gemini
// 3.x behind OmniRoute) spends a 1-token budget on reasoning, returns empty content,
// and the router answers 502 upstream_empty_response — a false "broken" badge for a
// setup that analyses fine. 16 leaves room for the reasoning prelude + one word.
export const TEST_MAX_TOKENS = 16;

export async function testConnection(preset, apiKey, baseUrl = preset.baseUrl, model = preset.defaultModels.cheap) {
  const base = String(baseUrl || '').replace(/\/$/, '');
  const host = hostnameOf(base);
  const local = isLocalBase(base);
  const useBridge = !local && preset.id !== 'custom' && BRIDGE_HOSTS.includes(host);
  // stream:false — same reason as chat.js: 9Router (and other routers) default to SSE
  // when the field is absent, and an SSE body would read as a false-green "OK" here.
  const body = { model, messages: [{ role: 'user', content: 'ping' }], max_tokens: TEST_MAX_TOKENS, stream: false };
  try {
    if (useBridge) {
      const headers = { 'Content-Type': 'application/json', 'X-Llm-Base-Url': base };
      if (apiKey) headers['X-Byok-Key'] = apiKey;
      const res = await globalThis.fetch('/yt/or', {
        method: 'POST', headers, body: JSON.stringify(body), signal: AbortSignal.timeout(10000),
      });
      const data = await res.json().catch(() => ({}));
      // The bridge always answers HTTP 200 once it successfully curls upstream — any
      // real failure (bad key, bad model, upstream 4xx/5xx) shows up as data.error
      // instead of a non-2xx status, so res.ok alone would report a false "OK". Prefer
      // the upstream's OWN status code when the error body carries one (OpenRouter/Groq
      // both nest it as error.code) so the UI's existing 401/403 "key salah" branch
      // still fires instead of showing a confusing "HTTP 200" for a rejected key.
      if (data && data.error) {
        const upstreamCode = typeof data.error === 'object' ? Number(data.error.code) : NaN;
        return {
          ok: false,
          status: Number.isFinite(upstreamCode) ? upstreamCode : res.status,
          error: (data.error?.message || data.error).toString(),
        };
      }
      return { ok: res.ok, status: res.status };
    }
    const headers = { 'Content-Type': 'application/json', ...(apiKey ? { [preset.authHeader]: `Bearer ${apiKey}` } : {}) };
    const res = await globalThis.fetch(base + '/chat/completions', {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(10000),
    });
    return { ok: res.ok, status: res.status };
  } catch (e) {
    return { ok: false, status: 0, error: e?.message || String(e) };
  }
}
